import { openDB } from "idb";
import { deepEqual } from "./threeWayMerge";
import { hasUnsavedEdit } from "./unsavedEdits";
import { emitSheetContentReplaced } from "./sheetContentEvents";

// Local-first cache for scribe-fe (card #419).
//
// IndexedDB is the source of truth the UI reads from; the network is a
// background sync engine that hydrates these caches (pull) and drains the
// outbox (push). This module owns the schema + a small repository API — no
// other file should call `idb` directly.
//
//   entity stores  — shows/classes/rides/tests/people/horses/scoreSheets,
//                    keyed by server id, filled by hydration.
//   outbox         — ordered log of pending mutations to push to score-api.
//   meta           — sync cursors + counters (e.g. the outbox sequence).
//   scoreSheetSaves— legacy whole-sheet retry queue (#364); retained and
//                    migrated forward until the outbox write-path (#422)
//                    supersedes it.

export const DB_NAME = "scribe-offline";
// v3 → v4 (card #139): `utilityEvents` — the last GET /ride/:id/utility-events
// poll result, keyed by rideId, so a reload during an offline period still
// shows the last-known BLOOD/LAMENESS flags instead of a blank banner.
// v4 → v5 (card #761): `judges` — a show's score-judge roster (GET /judge),
// cached so section-judge gating (Class.sectionJudgeAssignments, #759) can
// resolve a booth position's judge identity offline, the same way
// classes/rides/tests already work offline-first.
// v5 → v6 (card #1664): `catalogDefs` — the global test catalog (GET
// /catalog, ~148 rows, org-agnostic), same store shape as scoreadmin-fe's
// (card #1662). Deliberately a SEPARATE store from `tests` above: `tests` is
// the per-show, reconcile-enriched score-test rows scoring already depends
// on (GET /test/:id, sync.js); `catalogDefs` is the raw global test
// DEFINITIONS used only to resolve a display NAME off ride.testId/
// class.testId (classDisplay.js's resolveCatalogTestName/getRideTestName).
// See hooks/useCatalogDefs.js for the hydrate-once policy and the demotion
// order (table cache preferred once present, a caller-supplied fallback off
// the ride's own Test row for a scribe who's never been online since install
// — the build-time score-common/catalog bundle that used to fill that role
// retired in #1665, docs/score/catalog-authority.md section 5).
// v7 → v8 (card #2825): `shipLog` — one row per outbox op that has actually
// been acknowledged by score-api. Before this store existed, a successful
// push just deleted the outbox row (sync.js's old removeOutboxOp call at the
// site now replaced by recordShipment below) and nothing else remembered it
// happened, so a scribe had no way to answer "did ride 47's sheet ship, and
// when" — the only signal was the absence of an alarm, which is not
// evidence (docs/score/scribe-resilience.md gap G12). recordShipment writes
// this row and deletes the outbox row in ONE IndexedDB transaction spanning
// both stores, so the two can never disagree: either both happen or neither
// does (IndexedDB aborts the whole transaction on any error inside it).
// v6 → v7 (card #2361): `scribeSeats` — a durable, scribe-scoped record of
// "this sub is assigned to this class at these positions", written whenever
// the assignment is actually seen (login-time resolution, ClassList visits)
// and read by useAssignedPosition when the CLASS itself is a cold-cache miss
// offline. Deliberately a separate store from `classes`, not a second writer
// into it: `classes` rows written by different sources carry different
// completeness (sync.js's hydrator writes raw, unstamped rows; a scribe's own
// listClasses/batch fetch is fuller but POST /class/batch — see
// useScribeAssignments — returns RAW rows missing fields listClasses adds,
// e.g. scheduledTime/fvdPushState, so writing those into the shared store
// would downgrade an already-enriched cached row for every reader of
// `classes.get`). A scribe's own assignment doesn't need a full class row at
// all — just classId + position — so it gets its own tiny store instead.
// v8 → v9 (card #3014) — `tombstones`: one row per entityKey a scribe has
// explicitly discarded or sent to quarantine (discardOp/quarantineOp,
// sync.js), so removing the outbox op leaves a LOCAL trace instead of
// vanishing (docs/score/scribe-resilience.md gap G12, card #2826's own "In
// review" chip read most naturally as a quarantined sheet). Deliberately a
// SEPARATE store from `shipLog`, never a status value written into it:
// shipLog is proof of a server ACK (card #2825's invariant — a distinct
// local-only status there would muddy that), and a tombstone means the
// opposite, nothing was ever sent. Keyed by entityKey (upsert): a sheet
// discarded/quarantined more than once over a show day keeps only its most
// recent tombstone, which is all buildOutboxSheetRows (outboxSheets.js)
// ever reads.
export const DB_VERSION = 9;

// keyPath + optional indexes per store. Index value is the keyPath for that
// index (an array keyPath makes a compound index).
//
// #695 — `classes.showScribe` is a compound index on [showId, cachedForSub],
// the same pattern as `scoreSheets.rideJudge` below. `cachedForSub` is a
// write-time tag (see the `classes` repository export) stamping WHICH
// scribe's fetch cached a given row. The background hydrator (sync.js) writes
// classes via the unscoped `putAll` and never stamps this field itself, so a
// row it's the FIRST to cache is simply invisible to the compound index
// (IndexedDB excludes a record from an index when any component of a
// compound keyPath is undefined) — it can never surface on a scribe-scoped
// read, regardless of which scribe(s) previously used this device. (#2968:
// `putAll` merge-preserves an ALREADY-stamped row's cachedForSub rather than
// overwriting it away, so a row a scribe's own fetch had already tagged
// stays visible to that scribe's own scoped read across a later unscoped
// hydrate write — see bulkPutPreserving and the `classes` repository export.)
export const STORES = {
  shows: { keyPath: "id" },
  classes: {
    keyPath: "id",
    indexes: { showId: "showId", showScribe: ["showId", "cachedForSub"] },
  },
  rides: { keyPath: "id", indexes: { classId: "classId" } },
  tests: { keyPath: "id" },
  people: { keyPath: "id" },
  horses: { keyPath: "id" },
  scoreSheets: { keyPath: "id", indexes: { rideJudge: ["rideId", "judgeId"] } },
  outbox: { keyPath: "opId", indexes: { seq: "seq" } },
  meta: { keyPath: "key" },
  scoreSheetSaves: { keyPath: "key" },
  // card #139 — last-known utility events per ride, { rideId, events, updatedAt }.
  utilityEvents: { keyPath: "rideId" },
  // card #761 — score-judge roster rows (GET /judge), keyed by id, indexed by
  // showId so section-judge gating can resolve "who's the judge at position
  // C" offline via name-matching against Class.judgeAssignments.
  judges: { keyPath: "id", indexes: { showId: "showId" } },
  // card #1664 — GET /catalog, keyed by the def's stable slug id (same key
  // ride.testId/class.testId already resolve against, see
  // classDisplay.js's resolveCatalogTestName). Global, not org/show-scoped:
  // no index needed.
  catalogDefs: { keyPath: "id" },
  // card #2361 — this scribe's own class-assignment record, keyed by
  // `${classId}::${sub}` (a shared kiosk can hold more than one scribe's
  // rows). See the DB_VERSION comment above for why this is a separate store
  // from `classes` rather than a second writer into it.
  scribeSeats: { keyPath: "key" },
  // card #2825 — one row per acknowledged outbox op, written in the same
  // transaction that deletes the outbox row (see recordShipment below).
  // Auto-incrementing local id: unlike outbox's opId, the same entity can
  // ship more than once over a show day (create, then a later update), and
  // each is its own row rather than an upsert.
  shipLog: { keyPath: "id", autoIncrement: true, indexes: { opId: "opId" } },
  // card #3014 — see the DB_VERSION comment above. `{ entityKey, kind
  // ("discarded"|"quarantined"), at, cause }`; written atomically with the
  // outbox delete by recordTombstone below.
  tombstones: { keyPath: "entityKey" },
};

let dbPromise;

export function openDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        for (const [name, cfg] of Object.entries(STORES)) {
          const store = db.objectStoreNames.contains(name)
            ? tx.objectStore(name)
            : db.createObjectStore(name, {
                keyPath: cfg.keyPath,
                autoIncrement: !!cfg.autoIncrement,
              });
          for (const [idxName, keyPath] of Object.entries(cfg.indexes ?? {})) {
            if (!store.indexNames.contains(idxName)) {
              store.createIndex(idxName, keyPath);
            }
          }
        }
        // #3026: only the v1 -> v2 upgrade needs this migration. Gating on
        // `oldVersion >= 1` instead of the range it was written for re-ran
        // it on every later DB_VERSION bump (v8 -> v9 for #3014, etc.),
        // re-queuing any still-present legacy scoreSheetSaves row into the
        // outbox again and resetting outboxSeq back down to that row count.
        if (oldVersion >= 1 && oldVersion < 2) await migrateSavesToOutbox(tx);
      },
    });
  }
  return dbPromise;
}

// v1 → v2: fold any parked whole-sheet saves into the outbox so the sync
// engine can drain them. Copies (does not delete) — the legacy drain still
// reads scoreSheetSaves until #422 removes it, so nothing is stranded.
async function migrateSavesToOutbox(tx) {
  const saves = await tx.objectStore("scoreSheetSaves").getAll();
  if (!saves.length) return;
  const outbox = tx.objectStore("outbox");
  let seq = 0;
  for (const s of saves) {
    seq += 1;
    const entityKey = `${s.rideId}::${s.judgeId}`;
    const isTemp = typeof s.rideId === "string" && s.rideId.startsWith("temp");
    await outbox.put({
      opId: `scoreSheet:${entityKey}`,
      seq,
      type: s.serverId ? "updateScoreSheet" : "createScoreSheet",
      entityKey,
      tempId: isTemp ? s.rideId : null,
      payload: {
        rideId: s.rideId,
        judgeId: s.judgeId,
        serverId: s.serverId ?? null,
        body: s.body,
      },
      status: "pending",
      attempts: 0,
      createdAt: s.updatedAt ?? null,
    });
  }
  await tx.objectStore("meta").put({ key: "outboxSeq", value: seq });
}

// ── Generic repository ────────────────────────────────────────────────────

export async function get(store, key) {
  return (await openDb()).get(store, key);
}

export async function getAll(store) {
  return (await openDb()).getAll(store);
}

export async function put(store, value) {
  return (await openDb()).put(store, value);
}

export async function bulkPut(store, values) {
  if (!values?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const v of values) tx.store.put(v);
  await tx.done;
}

// #2968 — like bulkPut, but for each incoming row, if a row already exists
// under the same key and carries `preserveKey`, keep that field's EXISTING
// value on the write rather than letting the incoming row (which may not
// carry the field at all) blank it out. A plain `put` writes the whole
// object, so an unscoped writer (one that doesn't know about a field a
// different, narrower writer stamps) silently erases it on every row it
// touches. Used so the background hydrator's broad, unscoped classes.putAll
// can't wipe a scribe-scoped cachedForSub stamp a prior putAllForScribe
// already applied to the same row (see the `classes` repository below).
export async function bulkPutPreserving(store, values, preserveKey) {
  if (!values?.length) return;
  const db = await openDb();
  const keyPath = STORES[store]?.keyPath;
  const tx = db.transaction(store, "readwrite");
  for (const v of values) {
    const key = keyPath ? v[keyPath] : undefined;
    const existing = key !== undefined && key !== null ? await tx.store.get(key) : undefined;
    const preserved = existing?.[preserveKey];
    tx.store.put(preserved !== undefined ? { ...v, [preserveKey]: preserved } : v);
  }
  await tx.done;
}

// Content minus the tracked-ancestor field, for the divergence check below.
function stripSyncedSheet(row) {
  if (!row) return row;
  // eslint-disable-next-line no-unused-vars
  const { _syncedSheet, ...rest } = row;
  return rest;
}

// #3008 judge round 2 (B2) -- the ONE way to stamp a scoreSheets row with its
// tracked ancestor. `_syncedSheet` is local-cache-only: it must never appear
// INSIDE the ancestor itself, or the row's content can never deep-equal its
// ancestor again and bulkPutAncestorAware treats it as diverged forever
// (every later server row for that sheet is refused, so a reopened sheet
// shows whatever stale content the cache last held). Measured in
// confirm-score.cy.js #2810 AC3: an update's echoed response carried the
// cached row's `_syncedSheet` back, the success stamp nested it, and the
// next test's server row was skipped. Stripping both sides here keeps the
// invariant no matter which response (create/update/complete/confirm/
// panelSign, or a batch GET) carried the field.
export function withSyncedAncestor(content, ancestor) {
  return { ...stripSyncedSheet(content), _syncedSheet: stripSyncedSheet(ancestor) };
}

// Card #3015 -- the SAME contentDiverged test bulkPutAncestorAware already
// runs per-row (below), exposed so a reader (outboxSheets.js) can ask the
// same question this writer already answers, rather than re-deriving its own
// copy of the deepEqual/stripSyncedSheet plumbing. True for a scoreSheets row
// whose content has moved past the ancestor its OWN last server sync
// stamped -- the durable, DB-level signature of "this device holds an edit
// the server has not acknowledged", independent of the in-memory
// unsavedEdits registry (which never survives a reload) and independent of
// whether an outbox op for it exists right now (a pre-flight-blocked save
// never enqueues one at all, see useScoreSheet.js's persist()). A row with
// no `_syncedSheet` yet (never round-tripped through putAllFromServer/a
// synced write) reads as NOT diverged here -- see isLocalDraftScoreSheet for
// the provisional-row half of that case instead.
export function isDivergedFromSyncedAncestor(row) {
  return (
    row?._syncedSheet !== undefined &&
    !deepEqual(stripSyncedSheet(row), stripSyncedSheet(row._syncedSheet))
  );
}

// Card #3015 -- a scoreSheets row is a local draft (typed here, never reached
// the outbox as a live op or a shipment) when either:
//   - it's `provisional` (useScoreSheet.js's persist() stamps this on a
//     CREATE's local commit, keyed on the client-side entityKey, whenever
//     the create has no server id yet -- a pre-flight-blocked new sheet
//     never gets past this), or
//   - its content has diverged from its own tracked ancestor (an UPDATE's
//     local commit against a known serverId, when pre-flight blocked the
//     enqueue that would otherwise have advanced `_syncedSheet` to match).
// A row hydrated from the server and never edited here is neither: never
// provisional, and putAllFromServer/bulkPutAncestorAware only ever advances
// `_syncedSheet` alongside matching content, so the two never diverge on a
// read-only cache row.
export function isLocalDraftScoreSheet(row) {
  return Boolean(row?.provisional) || isDivergedFromSyncedAncestor(row);
}

// #3008 — the thing being protected is not a static field, it's whichever
// ancestor (and content) a local writer's edit is based on.
//
// #3008 judge round 1 (B1) driver decision, rule 1 -- ancestor and content
// move TOGETHER or not at all. An incoming server row may advance a row's
// `_syncedSheet` (the tracked common ancestor `toBody()` reads for
// `baseVersion` and sync.js's stale_write merge reads as the three-way
// merge's base) ONLY together with that row's content, and only when there
// is no divergence:
//   - no pending (queued, unsynced) outbox op for this exact
//     (rideId, judgeId) entity, AND
//   - no unsaved edit the open useScoreSheet hook has flagged for it (typed
//     but not yet enqueued -- the SAVE_DEBOUNCE_MS window; see
//     local/unsavedEdits.js), AND
//   - the existing cached row's content already deep-equals its own tracked
//     ancestor (or there is no existing row / no ancestor recorded yet, in
//     which case there is nothing to diverge from).
// Any of those failing means a local writer's edit is based on CURRENT
// local state, not on whatever this incoming row says. Advancing either
// field then would silently detach the next save's baseVersion from what
// the edit actually started from -- score-api's stale check would never
// fire, and whichever writer only IndexedDB knew lost the race gets
// silently overwritten (the exact HANDOFF 4 defect, and the B1 stale-cache-
// revalidate case the judge found on top of it). So when diverged, the row
// is left EXACTLY as it is -- both content and ancestor untouched -- and
// the next save's 409 + three-way merge (sync.js) is the recovery path.
//
// See #3008 HANDOFF 4 for the confirmed runtime trace (useRidesForClass.js's
// batch prefetch was doing exactly this, six times in one test, wiping a
// correct stamp useScoreSheet.js's fromApi() had just written) and judge
// round 1's B1 finding for the stale-cache-open case this rule closes.
async function bulkPutAncestorAware(store, rows, entityKeyOf) {
  if (!rows?.length) return;
  const db = await openDb();
  const tx = db.transaction([store, "outbox"], "readwrite");
  const storeHandle = tx.objectStore(store);
  const outbox = tx.objectStore("outbox");
  // #3008 judge round 1 (B1), driver decision rule 2 -- collected here and
  // emitted only after tx.done below, never inside the transaction loop:
  // sheetContentEvents' listeners (an open useScoreSheet hook) are plain
  // synchronous callbacks, but keeping the emit strictly outside the
  // IndexedDB transaction avoids ever depending on idb's own auto-commit
  // timing to decide when a subscriber is allowed to see the new content.
  const replaced = [];
  for (const row of rows) {
    const entityKey = entityKeyOf(row);
    const pendingOp = entityKey ? await outbox.get(`scoreSheet:${entityKey}`) : undefined;
    const existing = row?.id !== undefined && row?.id !== null
      ? await storeHandle.get(row.id)
      : undefined;
    const contentDiverged =
      existing?._syncedSheet !== undefined &&
      !deepEqual(stripSyncedSheet(existing), stripSyncedSheet(existing._syncedSheet));
    const diverged = Boolean(pendingOp) || hasUnsavedEdit(entityKey) || contentDiverged;
    if (diverged) continue;
    const stamped = withSyncedAncestor(row, row);
    storeHandle.put(stamped);
    if (entityKey) replaced.push([entityKey, stamped._syncedSheet]);
  }
  await tx.done;
  for (const [entityKey, row] of replaced) emitSheetContentReplaced(entityKey, row);
}

export async function queryByIndex(store, index, value) {
  return (await openDb()).getAllFromIndex(store, index, value);
}

export async function remove(store, key) {
  return (await openDb()).delete(store, key);
}

export async function bulkRemove(store, keys) {
  if (!keys?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const k of keys) tx.store.delete(k);
  await tx.done;
}

export async function clear(store) {
  return (await openDb()).clear(store);
}

// ── Meta (sync cursors / counters) ─────────────────────────────────────────

export async function getMeta(key) {
  const row = await get("meta", key);
  return row ? row.value : undefined;
}

export async function setMeta(key, value) {
  return put("meta", { key, value });
}

// ── Outbox (ordered pending mutations) ─────────────────────────────────────

// ── Ownership (card #1923) ───────────────────────────────────────────────
//
// The structural half of the WEC 2026-08-23 sync incident: scribe A finishes
// a class with writes still queued, signs out (which deliberately does NOT
// touch the outbox, #519/#1289), and scribe B signs in on the same kiosk —
// B's token would otherwise replay A's queued writes. The fix is an owner on
// every op, stamped at enqueue() time from whoever is CURRENTLY signed in.
//
// This module has no React/auth dependency of its own (it's imported by
// framework-agnostic sync.js and by plain store modules like manualRides.js
// that aren't hooks), so ScoreContext pushes the signed-in sub in here the
// same way it already pushes into syncEngine (setApi/setBrowserOnline) —
// see ScoreContext's effect on auth.user?.profile?.sub.
let currentOwnerSub = null;

export function setCurrentOwnerSub(sub) {
  currentOwnerSub = sub ?? null;
}

export function getCurrentOwnerSub() {
  return currentOwnerSub;
}

// An op belongs to "someone else" whenever its owner isn't EXACTLY the given
// sub. A missing `owner` (any row written before this card, or by the v1→v2
// scoreSheetSaves migration below, which never went through enqueue) is
// "unknown" — AC2 requires that never be read as "belongs to whoever happens
// to be signed in now".
//
// #1923 F2 (judge finding, fixed) — fail CLOSED: an op is held whenever its
// owner doesn't exactly match a KNOWN current sub. A falsy currentSub means
// no sub is known yet (signed out, or ScoreContext hasn't resolved a sub),
// so nothing can be verified as "mine" and everything is held, including
// ops whose own `owner` is also null/undefined — an unknown current sub
// never gets to wave an unknown-owner op through. `flush()` additionally
// bails out entirely when getCurrentOwnerSub() is null, so nothing that
// needs a bearer token even attempts to send while no one is signed in.
//
// This used to fail OPEN (`Boolean(currentSub) && ...`), which let every
// foreign/legacy op read as sendable while no current sub was known —
// exactly the hole AC2 names. The earlier fail-closed attempt was reverted
// because it broke 31 tests in sync.test.js/manualRideSyncStatus.test.js
// that build a syncEngine directly and never call setCurrentOwnerSub; those
// suites now seed an owner sub in setup (they mean "a scribe is signed in"),
// so the fail-closed behavior here is no longer in tension with them.
//
// #1923 F3 (judge finding, fixed) — `apiRef` is also NOT the safety net an
// earlier version of this comment claimed: ScoreContext.jsx wires
// syncEngine.setApi(apiAdapter) unconditionally on mount (a plain useEffect
// with no auth.user guard), and apiAdapter builds its Configuration with
// accessToken: undefined when there's no signed-in user yet — so flush()
// can have apiRef set with no real sub resolved. That claim has been
// removed; nothing in this file leans on it anymore.
export function isHeldOp(op, currentSub) {
  if (!currentSub) return true;
  return (op?.owner ?? null) !== currentSub;
}

// #2031 AC2 — the hard boundary a claim action must respect: an op is
// claimable only when it carries NO owner at all (a pre-#1923 legacy row, or
// the v1->v2 scoreSheetSaves migration below, neither of which ever went
// through enqueue's owner stamp). An op stamped with a DIFFERENT KNOWN sub is
// held for the same reason isHeldOp holds it, but must never read as
// claimable — conflating the two would let a scribe claim someone else's
// STILL-owned queued writes, the exact WEC 2026-08-23 hazard #1923 exists to
// prevent. Deliberately takes no currentSub: ownerless-ness is a fact about
// the op alone, true or false regardless of who is signed in right now.
export function isOwnerless(op) {
  return (op?.owner ?? null) === null;
}

// #2031 AC3 / #882 — a dependent op (carries a tempId, not itself a
// createRide) can never drain while its own createRide op is still queued:
// this is the exact same ordering-gate check flush()'s `ready` filter makes
// (see sync.js's #882 CRITICAL invariant comment) — pulled out here so the
// engine and the display-side "was this op withheld" logic share one
// definition instead of two copies of the same tempId-segment match
// drifting apart. Matches on the createRide op being present AT ALL,
// regardless of ITS status (pending, error, or terminal): a terminal
// createRide (a permanent 4xx, never auto-retried, #1921) still sits in the
// outbox until a human discards it, so it still blocks its dependent op —
// that is the "blocked forever" case the card names.
export function isBlockedByUnsyncedRide(op, allOps) {
  if (!op?.tempId || op.type === "createRide") return false;
  return allOps.some(
    (other) => other.type === "createRide" && other.tempId === op.tempId,
  );
}

// Enqueue or upsert an op. If `opId` already exists the payload is replaced
// but its original `seq` (FIFO position) is preserved — last-write-wins per
// entity, so a fresh score save overwrites the parked one without reordering.
// `owner` is always stamped fresh from the CURRENT sub (#1923): an upsert is
// always a new local write by whoever is signed in right now, same as status
// resetting to "pending" below.
//
// #2964 — every insert/upsert also bumps a monotonic `rev` stamp. flush()
// remembers the rev of the row it sent, and the remove-after-apply inside
// recordShipment is a compare-and-delete on that rev: a save that re-enqueues
// the SAME opId while the PUT/POST is in flight bumps the rev, so the ack of
// the older body can no longer delete the newer one as "drained" (silent
// score loss, this card's defect). Legacy rows written before this stamp
// have no rev; both sides reading undefined still compare equal.
//
// #2966 — the read-modify-write (including the seq allocation, whose old
// nextSeq helper opened its OWN meta transaction) runs inside ONE readwrite
// transaction spanning outbox + meta. The old get-then-put across separate
// transactions let two overlapping enqueues of the same opId (debounced
// persist + complete()'s persist, say) both read rev N and both stamp N+1;
// a flush peeking between the two puts then acked body A at rev N+1, body B
// landed at the SAME rev N+1, and recordShipment's compare-and-delete saw
// equal revs and dropped B as drained — the #2964 loss in a narrower window.
// Overlapping readwrite transactions on the same stores serialize in
// IndexedDB, so the second enqueue now reads the first one's row and every
// distinct write of one opId stamps a distinct rev.
export async function enqueue({ opId, type, entityKey, tempId = null, payload }) {
  const db = await openDb();
  const tx = db.transaction(["outbox", "meta"], "readwrite");
  const outbox = tx.objectStore("outbox");
  const existing = await outbox.get(opId);
  let seq = existing?.seq;
  if (seq === undefined) {
    const meta = tx.objectStore("meta");
    const cur = (await meta.get("outboxSeq"))?.value ?? 0;
    seq = cur + 1;
    meta.put({ key: "outboxSeq", value: seq });
  }
  const op = {
    opId,
    seq,
    type,
    entityKey,
    tempId,
    payload,
    status: "pending",
    attempts: existing?.attempts ?? 0,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    owner: currentOwnerSub,
    rev: (existing?.rev ?? 0) + 1,
  };
  outbox.put(op);
  await tx.done;
  return op;
}

// Pending ops in FIFO order (ascending seq).
export async function peekOutbox() {
  return (await openDb()).getAllFromIndex("outbox", "seq");
}

export async function getOutboxOp(opId) {
  return (await openDb()).get("outbox", opId);
}

export async function removeOutboxOp(opId) {
  return (await openDb()).delete("outbox", opId);
}

export async function updateOutboxOp(opId, patch) {
  const db = await openDb();
  const existing = await db.get("outbox", opId);
  if (!existing) return null;
  const merged = { ...existing, ...patch };
  await db.put("outbox", merged);
  return merged;
}

// #2964 — promote a still-queued createScoreSheet op into an
// updateScoreSheet against a now-known serverId, in ONE readwrite
// transaction. The old sync.js version did a getOutboxOp read and then a
// separate updateOutboxOp write; an enqueue landing in that window was
// overwritten by the stale `{...queued.payload}` patch (race 1 on the card).
// Reading and writing inside a single transaction closes that window, and
// deriving the promoted payload from `stored` (the row this transaction is
// writing) — never from a caller-supplied earlier read — means a save that
// re-enqueued the same opId with a newer body is carried through, not
// clobbered. `sentPayload` is only used to DETECT that nothing re-enqueued
// (stored payload still equals what was sent → no-op, flush removes it as a
// plain drained success). Returns the promoted row, or null when it did
// nothing (op gone, not a create, or unchanged).
export async function promoteOutboxCreateToUpdate(opId, sentPayload, serverId) {
  if (!serverId) return null;
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  const stored = await tx.store.get(opId);
  if (!stored || stored.type !== "createScoreSheet") {
    await tx.done;
    return null;
  }
  if (
    JSON.stringify(stored.payload ?? null) ===
    JSON.stringify(sentPayload ?? null)
  ) {
    await tx.done;
    return null;
  }
  const promoted = {
    ...stored,
    type: "updateScoreSheet",
    status: "pending",
    payload: { ...stored.payload, serverId },
  };
  tx.store.put(promoted);
  await tx.done;
  return promoted;
}

// #3008 -- resubmit a stale_write-refused updateScoreSheet op with its
// three-way-merged body, in ONE readwrite transaction: same "read + verify +
// write with no window a concurrent enqueue can land in unseen" pattern as
// promoteOutboxCreateToUpdate above, applied to the opposite case. That
// function patches only when the op's payload has CHANGED since it was sent
// (a newer enqueue landed, so ITS body must survive); this one patches only
// when the payload is STILL UNCHANGED since `sentPayload` -- the exact body
// the three-way merge (sync.js flush()) was computed against. If a fresh
// scribe edit already re-enqueued this opId while the stale_write round trip
// was in flight, `stored.payload` no longer matches, and this merge result
// (computed against the now-stale local body) is not this call's to
// overwrite it with -- it returns null and leaves the newer body exactly as
// queued, for the next flush pass to attempt (and merge, if it stale_writes
// too) fresh, against whatever ancestor IT was actually built from. Cannot
// reuse promoteOutboxCreateToUpdate itself: it is type-gated to
// "createScoreSheet" and its compare direction is inverted for this case.
//
// #3008 judge round 1 (B3) -- `ancestor` (the server's CURRENT sheet from
// the 409 that triggered this resubmit, i.e. sync.js's `serverCurrent`) is
// stamped onto the resubmitted op's OWN payload (`payload.ancestor`), not
// just used to build `mergedBody`. If this same op stale_writes AGAIN before
// it succeeds, sync.js must merge its (already-merged) body against THIS
// ancestor, not the local scoreSheets row's `_syncedSheet` -- that row is
// only re-stamped on a SUCCESSFUL save, so it would still be the boundary
// the FIRST merge was built from, not the one this resubmit actually
// reflects. Omitted (undefined) when the caller has none to carry, so an
// existing caller/test that doesn't pass it sees no new payload key.
export async function resubmitMergedScoreSheetBody(opId, sentPayload, mergedBody, ancestor) {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  const stored = await tx.store.get(opId);
  if (!stored || stored.type !== "updateScoreSheet") {
    await tx.done;
    return null;
  }
  if (
    JSON.stringify(stored.payload ?? null) !== JSON.stringify(sentPayload ?? null)
  ) {
    await tx.done;
    return null;
  }
  const resubmitted = {
    ...stored,
    status: "pending",
    attempts: 0,
    nextRetryAt: null,
    payload: {
      ...stored.payload,
      body: mergedBody,
      ...(ancestor !== undefined ? { ancestor } : {}),
    },
  };
  tx.store.put(resubmitted);
  await tx.done;
  return resubmitted;
}

// #2037 (repair round) — stamp `inFlight` atomically: ONE readwrite
// transaction that re-reads the op and puts only if it still exists. A
// get-then-put through updateOutboxOp above leaves a window where a discard
// completes between the flush loop's peek and the stamp; the caller would
// then POST from a stale snapshot and recreate the very orphan the marker
// exists to prevent. Returns null (and writes NOTHING — never resurrects a
// discarded op) when the op is gone.
export async function markOutboxOpInFlight(opId) {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  const existing = await tx.store.get(opId);
  if (!existing) {
    await tx.done;
    return null;
  }
  const merged = { ...existing, inFlight: true };
  tx.store.put(merged);
  await tx.done;
  return merged;
}

// #2037 (repair round) — the discard side of the same race: delete the
// createRide op (plus any dependent ops found by the guard) in ONE readwrite
// transaction that re-reads the op first and REFUSES when its inFlight
// marker is set — the engine may be mid-POST right now even though the
// guard's earlier peek didn't see the stamp yet. Refusing deletes nothing
// (the transaction commits unchanged), so all stores stay consistent for
// the retry once the POST lands.
export async function removeOutboxOpsUnlessInFlight(opId, alsoOpIds = []) {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  const existing = await tx.store.get(opId);
  if (existing?.inFlight) {
    await tx.done;
    return { removed: false, op: existing };
  }
  tx.store.delete(opId);
  for (const id of alsoOpIds) tx.store.delete(id);
  await tx.done;
  return { removed: true, op: existing ?? null };
}

// ── Ship log (card #2825) ──────────────────────────────────────────────────
//
// One row per outbox op that score-api has actually acknowledged. Written in
// the SAME IndexedDB transaction that deletes the op's outbox row, so the two
// can never disagree: a transaction spanning two stores is atomic in
// IndexedDB (every request in it commits together, or none does — an error
// thrown by any request aborts the whole transaction and rolls back every
// write already issued in it, per the spec). sync.js's flush() calls this
// instead of the old bare removeOutboxOp once applyOp has succeeded.
//
// `op` is the outbox row being shipped (for type/entityKey/tempId); `result`
// is whatever applyOp resolved (serverId + targetIds, both nullable — an
// event op like clearUtilityEvent produces no server id of its own).
export async function recordShipment(op, result) {
  const db = await openDb();
  const tx = db.transaction(["shipLog", "outbox"], "readwrite");
  // A request that fails as a SYNCHRONOUS throw (an invalid key, for
  // instance) never gets far enough to become a rejected IndexedDB request,
  // so it never aborts the transaction on its own: the browser still sees a
  // transaction with no more pending requests and auto-commits it, keeping
  // whatever earlier requests in it already succeeded. Concretely: the
  // shipLog add above can resolve, then the outbox delete below throws
  // synchronously, and without an explicit abort here the shipLog row still
  // commits while the outbox row is never removed. tx.abort() rolls back
  // every request already issued on this transaction, committed or not, so
  // the two writes stay atomic even when the failure is this kind of throw.
  try {
    await tx.objectStore("shipLog").add({
      opId: op.opId,
      type: op.type,
      entityKey: op.entityKey ?? null,
      targetIds: result?.targetIds ?? null,
      serverId: result?.serverId ?? null,
      acknowledgedAt: new Date().toISOString(),
      // Card #3012 -- additive: the op's own PRE-SHIP attempts/failureCode/
      // errorClass, i.e. whatever an earlier failed attempt of this same op
      // stamped on it before it went on to succeed (sync.js's failure branch
      // writes these onto the outbox row; `op` here is the row as flush
      // peeked it right before applyOp, so a never-failed op simply carries
      // its defaults). outboxSheets.js reads failureCode off this row to
      // show a shipped sheet as "Resolved" with its cause (getFailureAction)
      // instead of silently looking identical to one that shipped clean on
      // the first try -- the #2825 shipLog row already existed, this is new
      // fields on it, not a new store or DB_VERSION bump.
      attempts: op.attempts ?? 0,
      failureCode: op.failureCode ?? null,
      errorClass: op.errorClass ?? null,
    });
    // #2964 — compare-and-delete, atomic with the ship-log write above and
    // inside the SAME readwrite transaction: delete the outbox row ONLY when
    // the stored op is still the one that was applied (its `rev` equals the
    // rev flush sent). A save that re-enqueued the same opId mid-flight
    // bumped the rev, so the newer body is left queued for the next drain
    // instead of being deleted as "drained" — the silent score loss this
    // card fixes. Returns { removed } so flush can lift its `processed`
    // mark and re-send the newer body in the same pass.
    const outbox = tx.objectStore("outbox");
    const stored = await outbox.get(op.opId);
    const removed =
      !stored || (stored.rev ?? undefined) === (op.rev ?? undefined);
    if (removed) await outbox.delete(op.opId);
    await tx.done;
    return { removed };
  } catch (err) {
    // If `err` came from a request that failed asynchronously (a
    // ConstraintError, say) rather than a synchronous throw, the browser has
    // already auto-aborted this transaction by the time we get here. Calling
    // tx.abort() again on an already-finished transaction throws
    // InvalidStateError, which would replace `err` below with that
    // InvalidStateError instead of the real failure the caller needs to see
    // (carried over from #2825's judge, card #2829). The abort we wanted
    // already happened; swallow the redundant one.
    try {
      tx.abort();
    } catch {
      // already aborted/committed — nothing to do
    }
    // idb caches a `tx.done` promise the moment the transaction is created
    // (see cacheDonePromiseForTransaction in idb's source), whether or not
    // anyone ever reads `.done` — abort() makes that cached promise reject,
    // and with nothing here awaiting it that reject would otherwise surface
    // as an unhandled rejection. Swallow it deliberately; `err` below is the
    // real error this call reports to its caller.
    await tx.done.catch(() => {});
    throw err;
  }
}

export const shipLog = {
  getAll: () => getAll("shipLog"),
  byOpId: (opId) => queryByIndex("shipLog", "opId", opId),
};

// Card #3014 — the atomic counterpart to discardOp/quarantineOp's old bare
// removeOutboxOp call: writes a tombstone row (upsert on entityKey) and
// deletes the outbox row in ONE readwrite transaction, same "both or
// neither" guarantee recordShipment gives shipLog+outbox above, so a
// tombstone can never exist for an op that's still queued, or vice versa.
// Never touches `shipLog` (the 2825 invariant stays untouched: shipLog is
// proof of a server ack, and this path is the opposite — nothing was ever
// sent). Called with a null/undefined entityKey is a caller bug (every real
// outbox op carries one, enqueue always stamps it) and would throw on the
// tombstones.put (keyPath can't be undefined) — callers guard on
// `op.entityKey` before calling this, same pattern flush()'s own callers use
// elsewhere in this file.
export async function recordTombstone(entityKey, kind, cause, opId) {
  const db = await openDb();
  const tx = db.transaction(["tombstones", "outbox"], "readwrite");
  try {
    await tx.objectStore("tombstones").put({
      entityKey,
      kind,
      at: new Date().toISOString(),
      cause: cause ?? null,
    });
    await tx.objectStore("outbox").delete(opId);
    await tx.done;
  } catch (err) {
    // Same synchronous-throw-vs-auto-abort subtlety as recordShipment above
    // (its own comment explains why both the redundant abort() AND the
    // tx.done swallow are needed).
    try {
      tx.abort();
    } catch {
      // already aborted/committed — nothing to do
    }
    await tx.done.catch(() => {});
    throw err;
  }
}

export const tombstones = {
  getAll: () => getAll("tombstones"),
  get: (entityKey) => get("tombstones", entityKey),
};

// ── Per-entity accessors (thin wrappers used by the read hooks in #421) ─────

export const shows = {
  get: (id) => get("shows", id),
  getAll: () => getAll("shows"),
  // #1736 — org-scoped read for useShows. This store is a CROSS-ORG cache:
  // putAll below is an upsert that never evicts, so shows from every org the
  // device has ever loaded accumulate in it forever. An unscoped getAll
  // therefore keeps serving the PREVIOUS org's shows after a switch, and lets
  // a scribe pick a show that isn't in the active org at all. Same scoping
  // intent as classes' byShowForScribe (#695), filtered in JS rather than
  // behind a new IndexedDB index because an index needs a schema version bump
  // and the show list is only tens of rows.
  // A falsy orgId matches NOTHING rather than every row that happens to lack
  // the field: `r.orgId === undefined` would otherwise make a pre-orgId legacy
  // row look like a hit for "no active org yet".
  //
  // #1746 — #1736 added no IndexedDB migration and did not bump DB_VERSION,
  // so a device that cached shows BEFORE v0.29.1 has rows with no `orgId` at
  // all, which can never match the filter above. Online this heals itself
  // (putAll re-stamps orgId from score-api's response on the next fetch), but
  // offline the scoped read comes back empty and the show picker has nothing
  // to fall back on: a regression against pre-#1736 behaviour. So: if the
  // org-scoped read comes back empty AND the store holds rows with no orgId
  // at all, serve those legacy rows instead of nothing.
  //
  // Read-time fallback was chosen over a DB_VERSION migration. Rejected
  // reason is NOT that the active org is unknowable at upgrade time --
  // ActiveOrgContext.jsx persists it synchronously to localStorage
  // (`common-fe.activeOrgId`), so an upgrade callback could read it. The
  // real problem is what pre-#1736 rows the migration would be stamping: per
  // the comment above, this store is a cross-org pile that never evicts, so
  // a multi-org device's legacy rows can belong to MORE than one org. Any
  // single org id the migration picks would mislabel every other org's rows
  // in that pile as the chosen org's own. A WRONGLY-stamped row is worse
  // than an unstamped one: the fallback below only ever serves `!r.orgId`
  // rows, so it can never catch a mislabeled row, which is instead served to
  // the wrong org as a legitimate scoped match. A later fetch does re-stamp
  // (see the putAll note further down), but only for rows that org's current
  // list still returns -- a mislabeled row for a show no org's list returns
  // any more is never corrected at all. Dropping the rows instead just
  // recreates the same offline gap once.
  //
  // This read-time fallback accepts a narrower, self-healing version of that
  // same cost instead, same trade the card names explicitly: on a device
  // that cached MORE THAN ONE org's shows before v0.29.1, all of it lives in
  // this store unstamped, so if org B has no stamped rows of its own yet,
  // this fallback cannot tell org A's legacy rows from org B's and will
  // serve org A's pre-v0.29.1 shows under org B. The window closes for the
  // READING org once that org itself gets a real online fetch (it then has
  // stamped rows of its own, so `scoped.length` is non-zero and the fallback
  // never runs for it), or once EVERY OTHER org holding legacy rows in the
  // pile has: putAll re-stamps a fetching org's rows with orgId, so they stop
  // matching the `!r.orgId` filter and stop being eligible for anyone else's
  // fallback. Note one other org's fetch is NOT enough on its own: with three
  // or more pre-v0.29.1 orgs in the pile, org A's fetch clears only org A's
  // rows, and org C's legacy rows are still served under org B until org B or
  // org C fetches too. It never widens #1736's fix for the case #1736
  // actually covers (rows already carrying an orgId): only orgId-less rows
  // are ever served this way, never another org's orgId-stamped rows.
  byOrg: async (orgId) => {
    if (!orgId) return [];
    const all = await getAll("shows");
    const scoped = all.filter((r) => r?.orgId === orgId);
    if (scoped.length) return scoped;
    return all.filter((r) => r && !r.orgId);
  },
  putAll: (rows) => bulkPut("shows", rows),
};

export const classes = {
  get: (id) => get("classes", id),
  byShow: (showId) => queryByIndex("classes", "showId", showId),
  // #695 — scribe-scoped read for useClassesForShow: only rows THIS scribe's
  // own fetch tagged (see putAllForScribe) can ever come back here, so a
  // stale/cross-scribe row from a previous login on a shared device can't
  // surface even before any client-side assignment filter runs.
  byShowForScribe: (showId, sub) => queryByIndex("classes", "showScribe", [showId, sub]),
  // #2968 — like putAll below, a single-row put has the same unscoped-writer
  // problem: a caller that doesn't carry cachedForSub would otherwise blank
  // an already-stamped row on this store. Merge-preserve it here too so
  // classes.put can't reintroduce the bug through a write path putAll's own
  // fix doesn't cover.
  put: (row) => bulkPutPreserving("classes", [row], "cachedForSub"),
  // #695 — like putAll, but stamps each row with the scribe (`sub`) whose
  // fetch produced it, so byShowForScribe can find it again for THAT scribe
  // only. Used by useClassesForShow; the generic background hydrator
  // (sync.js) intentionally keeps calling the unscoped putAll below (broad
  // offline cache — a different purpose than the "My Classes" screen), not
  // this scribe-scoped write.
  //
  // #2968 — that unscoped putAll used to mean a literal `bulkPut`: a straight
  // IndexedDB `put` overwrites the whole row, and hydrate's unscoped class
  // list never carries `cachedForSub`, so its 5s-interval writes (sync.js
  // hydrate(), sync.js:82 flushIntervalMs) silently unstamped every class
  // row already tagged by this scribe's own online "My Classes" fetch. Fix
  // chosen (of the three options the card names): (a) make the unscoped
  // write itself merge-preserve an existing stamp (bulkPutPreserving),
  // rather than (b) routing hydrate through the scribe-scoped path — hydrate
  // deliberately fetches the FULL unscoped class list for the "all classes"
  // search modal (#694) and has no single scribe sub in scope for a walk
  // across every show — or (c) a read-time fallback like shows.byOrg
  // (db.js:615) — that would only paper over the symptom on THIS read path
  // and leave the AC2 requirement ("keeps cachedForSub on every write path")
  // unmet for any future reader of this store. (a) also can't leak a stamp
  // cross-scribe on a shared/kiosk device: byShowForScribe's index match
  // requires the EXACT sub, so a preserved stamp for scribe A stays simply
  // invisible to scribe B's own query until scribe A's or B's own
  // putAllForScribe overwrites it.
  putAll: (rows) => bulkPutPreserving("classes", rows, "cachedForSub"),
  putAllForScribe: (rows, sub) =>
    bulkPut(
      "classes",
      (rows ?? []).map((row) => ({ ...row, cachedForSub: sub })),
    ),
};

// card #2361 — this scribe's own class-assignment record: classId + which
// position(s) they're seated at, WITHOUT a full class row. Populated wherever
// the assignment is actually observed (useScribeAssignments at login,
// useClassesForShow on a ClassList visit) so useAssignedPosition can resolve
// a cold-class-cache offline read without depending on the (possibly never
// warm) `classes` store at all. See the DB_VERSION/STORES comments above for
// why this is its own store.
export const scribeSeats = {
  get: (classId, sub) => get("scribeSeats", `${classId}::${sub}`),
  // rows: [{ classId, positions }]. A class with zero positions for this sub
  // is simply not written — an absent row and a written-empty row must read
  // the same (both "no local record"), so there's no reason to store the
  // second shape.
  putAllForScribe: (rows, sub) =>
    bulkPut(
      "scribeSeats",
      (rows ?? [])
        .filter((r) => r.classId && r.positions?.length)
        .map((r) => ({ key: `${r.classId}::${sub}`, classId: r.classId, sub, positions: r.positions })),
    ),
  // card #2361 — deletes this scribe's own seat row for classIds where a
  // fresh, UNFILTERED read (useScribeAssignments's listClassesForShows,
  // which returns every class across the org's shows regardless of who's
  // assigned) positively confirms the sub is no longer named in
  // scribeAssignments at all. Without this a revoked assignment's row
  // survives forever: seatFallback (useAssignedPosition) would keep
  // resolving it offline against a cold class row, letting a former scribe
  // start (and, on reconnect, sync) a scoresheet for a seat that isn't
  // theirs any more. useClassesForShow must NEVER call this — its own fetch
  // is `mine=true`-scoped, so a class's absence there proves nothing about
  // whether the assignment was actually revoked.
  pruneForScribe: (classIds, sub) =>
    bulkRemove(
      "scribeSeats",
      (classIds ?? []).map((classId) => `${classId}::${sub}`),
    ),
};

// card #761 — a show's score-judge roster, cache-first like classes/rides.
export const judges = {
  get: (id) => get("judges", id),
  byShow: (showId) => queryByIndex("judges", "showId", showId),
  putAll: (rows) => bulkPut("judges", rows),
};

// card #1664 — the global test catalog. Hydration policy: refetch at EVERY
// app-init, not gated on the store already being non-empty (see
// hooks/useCatalogDefs.js — this deliberately FORKS from scoreadmin-fe's
// once-if-empty policy, card #1662, so an admin's correction reaches an
// already-hydrated scribe on the next restart with no redeploy). Reads stay
// cache-first regardless: `putAll` below only runs when the refetch
// actually returns rows, so a genuinely empty GET /catalog response (`[]`)
// or a failed/hung request simply leaves the existing store untouched
// rather than wiping it.
export const catalogDefs = {
  get: (id) => get("catalogDefs", id),
  getAll: () => getAll("catalogDefs"),
  putAll: (rows) => bulkPut("catalogDefs", rows),
};

export const rides = {
  get: (id) => get("rides", id),
  byClass: (classId) => queryByIndex("rides", "classId", classId),
  put: (row) => put("rides", row),
  putAll: (rows) => bulkPut("rides", rows),
  remove: (id) => remove("rides", id),
};

export const tests = {
  get: (id) => get("tests", id),
  put: (row) => put("tests", row),
  putAll: (rows) => bulkPut("tests", rows),
};

export const people = {
  get: (id) => get("people", id),
  getAll: () => getAll("people"),
  // #906 — useScoreSheet's fromApi() write-through calls this singular put()
  // on every revalidation that resolves a rider; it was missing (only
  // putAll existed), so that TypeError silently aborted fromApi() before its
  // apply(apiCtx) ever landed — stranding the sheet on stale cached data
  // (e.g. a class fetched before judgeIds was added, #733) forever.
  put: (row) => put("people", row),
  putAll: (rows) => bulkPut("people", rows),
};

export const horses = {
  get: (id) => get("horses", id),
  getAll: () => getAll("horses"),
  // #906 — see people.put above; same missing method, same silent abort.
  put: (row) => put("horses", row),
  putAll: (rows) => bulkPut("horses", rows),
};

export const scoreSheets = {
  get: (id) => get("scoreSheets", id),
  // Card #3015 -- every cached score sheet, any ride/judge, for the /outbox
  // page's local-draft sweep (buildOutboxSheetRows): it needs to find a row
  // with no live outbox op and no ship history at all, so it can't start
  // from either of those and must scan the whole store, same as `forRide`
  // already does for its own (rideId-scoped) reason.
  getAll: () => getAll("scoreSheets"),
  byRideJudge: (rideId, judgeId) =>
    queryByIndex("scoreSheets", "rideJudge", [rideId, judgeId]),
  put: (row) => put("scoreSheets", row),
  putAll: (rows) => bulkPut("scoreSheets", rows),
  // #3008 — the ONE write path for scoreSheets rows that came FROM THE
  // SERVER (a GET/list response body, a batch prefetch — anything that is
  // not itself a local edit's own success stamp, which already sets
  // `_syncedSheet` explicitly via `put`). Every such writer must route
  // through this, never the raw `putAll` above, so a batch write can never
  // erase or wrongly advance another write's tracked ancestor out from
  // under a pending local edit. See bulkPutAncestorAware's doc comment and
  // #3008 HANDOFF 4.
  putAllFromServer: (rows) =>
    bulkPutAncestorAware(
      "scoreSheets",
      rows,
      (row) => (row?.rideId && row?.judgeId ? `${row.rideId}::${row.judgeId}` : null),
    ),
  // #2756 — createScoreSheet's success path rekeys the provisional
  // entityKey-keyed row persist() wrote (useScoreSheet.js) by removing it
  // after caching the server row; remapTempRide rekeys one the same way.
  remove: (id) => remove("scoreSheets", id),
  // card #1922 — every cached score sheet for a ride, any judge. Used by the
  // manual-ride discard guard (store/manualRides.js), which needs "does this
  // ride have ANY score data" without knowing which judge scored it. No new
  // index: rideJudge is compound on [rideId, judgeId] so it can't answer a
  // rideId-only query, and this store is small enough to filter in JS rather
  // than justify a DB_VERSION bump for a rideId-alone index.
  forRide: async (rideId) =>
    (await getAll("scoreSheets")).filter((s) => s?.rideId === rideId),
};

// card #139 — last-known GET /ride/:id/utility-events result per ride, so a
// reload during an offline period shows what was last seen rather than
// nothing. `row` is { rideId, events, updatedAt }.
export const utilityEvents = {
  get: (rideId) => get("utilityEvents", rideId),
  put: (row) => put("utilityEvents", row),
};

// #2111 — the WHOLE local database, every store in STORES (entity caches,
// the outbox, meta, everything), as one plain object keyed by store name.
// Replaces the old "Copy outbox" export (formatOutboxForExport in
// outboxDetail.js, which only ever covered the outbox store): a scribe
// diagnosing a stuck kiosk with no devtools needs to hand over more than the
// outbox — a class/ride/scoreSheet cache that never hydrated, or hydrated
// wrong, is just as real a cause of a stuck sync as a bad outbox row, and the
// old export had no way to show that. Read-only; iterates STORES directly
// (not a hardcoded list) so a future store never has to be added here by
// hand to stay covered.
export async function dumpDatabase() {
  const storeNames = Object.keys(STORES);
  const entries = await Promise.all(
    storeNames.map(async (name) => [name, await getAll(name)]),
  );
  return {
    dbName: DB_NAME,
    dbVersion: DB_VERSION,
    exportedAt: new Date().toISOString(),
    stores: Object.fromEntries(entries),
  };
}

// Shared by _resetDbForTests and rebuildDatabase below: drop the cached
// connection and delete the whole DB_NAME database from IndexedDB.
async function closeAndDeleteDb() {
  if (dbPromise) (await dbPromise).close();
  dbPromise = undefined;
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve();
  });
}

// Test-only: drop the cached connection + delete the database so each test
// starts from a known-empty state.
export async function _resetDbForTests() {
  await closeAndDeleteDb();
  // #1923 — currentOwnerSub is module state, not DB state; a test that stamps
  // an owner (or leaves one signed in) must not leak into the next test.
  currentOwnerSub = null;
}

// Test-only (card #2825 AC4): close the cached connection WITHOUT deleting
// the database, then drop the cached promise so the next openDb() call opens
// a fresh connection against the SAME underlying data. This is what actually
// happens across a real page reload (module state resets, IndexedDB storage
// on disk does not) — unlike _resetDbForTests above, which wipes the data
// too and would prove nothing about persistence.
export async function _reopenDbForTests() {
  if (dbPromise) (await dbPromise).close();
  dbPromise = undefined;
}

// #1902 — support-only recovery: delete the whole scribe-offline database and
// reopen it empty. Deliberately does NOT touch currentOwnerSub (unlike
// _resetDbForTests above) — the scribe stays signed in through a rebuild, so
// the outbox's ownership stamping must keep matching whoever is still
// signed in once local/rebuild.js re-hydrates. Callers (local/rebuild.js)
// are responsible for confirming the outbox is empty BEFORE calling this;
// it unconditionally wipes.
export async function rebuildDatabase() {
  await closeAndDeleteDb();
  await openDb();
}
